import { getPurchaseEligibility } from "~/utils/purchase-eligibility.utils";

export const useCollectiblesController = () => {
  const userStore = useUserStore();
  const userCache = useUserCache();
  const GFALExStore = useGFALExStore();
  const { marketplaceFeesPercent, minimumPrice } = useRuntimeConfig().public;
  const { $serverApi } = useNuxtApp();
  const { web3Sign } = useWeb3();

  const isExpectedCollectibleNotFound = (error: unknown) => {
    const statusCode = (error as { statusCode?: number; response?: { status?: number } })
      ?.statusCode;
    const responseStatus = (error as { response?: { status?: number } })?.response
      ?.status;
    return statusCode === 404 || responseStatus === 404;
  };

  const withPurchaseEligibility = <T extends BaseMarketItem>(item: T): T => {
    const eligibility = getPurchaseEligibility(
      {
        priceGFAL: item.price?.GFAL,
        isPurchasable: item.isPurchasable,
        purchaseBlockReason: item.purchaseBlockReason,
      },
      {
        marketplaceFeesPercent: Number(marketplaceFeesPercent),
        minimumPriceUSD: Number(minimumPrice),
        gfalExRate: Number(GFALExStore.GFALExRate),
      }
    );

    return {
      ...item,
      ...eligibility,
    };
  };

  const getCollectibles = async (
    merchantId: string,
    page: number = 1,
    pageSize: number = 10,
    listed: boolean,
    filters: string = ""
  ) => {
    try {
      const response = await $serverApi<GFALResponse<Collectibles | null>>(
        `/api/collectibles?merchantId=${merchantId}&page=${page}&pageSize=${pageSize}&listed=${listed}&groupBy=SET${
          filters ? `${filters}` : "&sort=LISTED_TIME&order=DESC"
        }`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );
      const userIds = new Set<string>();
      const records = (response.payload?.records || []).map(withPurchaseEligibility);
      for (const collectible of records) {
        userIds.add(collectible.gfalId);
      }
      await userCache.addUserIds(Array.from(userIds));

      if (response.payload) {
        return {
          ...response.payload,
          records,
        };
      }

      return {
        total: 0,
        records: [],
        page: 0,
        totalPages: 0,
        ungroupedTotal: 0,
      };
    } catch (error) {
      console.error("error", error);
      return {
        total: 0,
        records: [],
        page: 0,
        totalPages: 0,
        ungroupedTotal: 0,
      };
    }
  };

  const getCheapestCollectibleInSet = async (
    merchantId: string,
    setId: string,
    listed: boolean
  ) => {
    try {
      const currentUser = userStore.id;
      const fetchCheapest = async (listedValue: boolean) => {
        let page = 1;
        const pageSize = 1;
        while (true) {
          const response = await $serverApi<GFALResponse<Collectibles | null>>(
            `/api/collectibles?merchantId=${merchantId}&setId=${setId}&page=${page}&pageSize=${pageSize}&listed=${listedValue}&groupBy=SET&sort=PRICE&order=ASC`,
            {
              headers: constructHeaders(userStore.authToken),
            }
          );
          const records = (response.payload?.records || []).map(
            withPurchaseEligibility
          );
          const userIds = new Set<string>();
          for (const collectible of records) {
            userIds.add(collectible.gfalId);
          }
          await userCache.addUserIds(Array.from(userIds));
          if (!records.length) return null;
          const collectible = records[0];
          if (collectible && collectible.gfalId !== currentUser) {
            return collectible;
          }
          page++;
        }
      };
      let result = await fetchCheapest(listed);
      if (result) return result;
      result = await fetchCheapest(!listed);
      return result;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const getCollectiblesCount = async (
    merchantId: string,
    listed: boolean,
    filters: string = ""
  ) => {
    try {
      const response = await $serverApi<GFALResponse<Collectibles | null>>(
        `/api/collectibles?merchantId=${merchantId}&listed=${listed}&page=1&pageSize=1&groupBy=SET${
          filters ? `${filters}` : ""
        }`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response.payload?.ungroupedTotal || response.payload?.total || 0;
    } catch (error) {
      console.error("error", error);
      return 0;
    }
  };

  const getStackedCollectibles = async (
    merchantId: string,
    page: number = 1,
    pageSize: number = 10,
    listed: boolean,
    filters: string = "",
    setId: string
  ) => {
    try {
      const response = await $serverApi<GFALResponse<Collectibles | null>>(
        `/api/collectibles?merchantId=${merchantId}&listed=${listed}&setId=${setId}&page=${page}&pageSize=${pageSize}${
          filters ? `${filters}` : "&sort=PRICE&order=ASC"
        }`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );

      const userIds = new Set<string>();
      const records = (response.payload?.records || []).map(withPurchaseEligibility);
      for (const collectible of records) {
        userIds.add(collectible.gfalId);
      }
      await userCache.addUserIds(Array.from(userIds));

      if (response.payload) {
        return {
          ...response.payload,
          records,
        };
      }

      return {
        total: 0,
        records: [],
        page: 0,
        totalPages: 0,
      };
    } catch (error) {
      console.error("error", error);
      return { total: 0, records: [], page: 0, totalPages: 0 };
    }
  };

  const getStackedCollectiblesCount = async (
    merchantId: string,
    listed: boolean,
    setId: string
  ) => {
    try {
      const response = await $serverApi<GFALResponse<Collectibles | null>>(
        `/api/collectibles?merchantId=${merchantId}&listed=${listed}&setId=${setId}&page=1&pageSize=1`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );

      return response.payload?.total || 0;
    } catch (error) {
      console.error("error", error);
      return 0;
    }
  };

  const getCollectibleById = async (id: string) => {
    try {
      const response = await $serverApi<GFALResponse<Collectible | null>>(
        `/api/collectibles/${id}`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );
      if (!response.payload) return null;
      return withPurchaseEligibility(response.payload);
    } catch (error) {
      if (isExpectedCollectibleNotFound(error)) {
        console.log(`Collectible lookup for ${id} returned 404; treating as expected for GFAL-owned items.`);
        return null;
      }
      console.error("error", error);
      return null;
    }
  };

  const getCollectibleSet = async (merchantId: string, setId: string) => {
    try {
      const response = await $serverApi<GFALResponse<CollectibleSet | null>>(
        `/api/merchants/${merchantId}/sets/${setId}`,
        {
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response.payload;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const setCollectibleAsViewed = async (collectibleId: string) => {
    try {
      const response = await $serverApi<GFALResponse<null>>(
        `/api/collectibles/${collectibleId}/view`,
        {
          method: "POST",
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response.payload;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const getCollectibleOffers = async (
    collectibleId: string,
    page: number,
    pageSize: number
  ) => {
    try {
      const response = await $serverApi<GFALResponse<OfferPayload | null>>(
        `/api/collectibles/${collectibleId}/offers`,
        {
          query: {
            page,
            pageSize,
            sort: "AMOUNT",
            order: "DESC",
          },
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const getCollectibleHighestOffer = async (collectibleId: string) => {
    try {
      const response = await $serverApi<GFALResponse<OfferPayload | null>>(
        `/api/collectibles/${collectibleId}/offers`,
        {
          query: {
            page: 1,
            pageSize: 1,
            sort: "AMOUNT",
            order: "DESC",
          },
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response.payload?.records[0] || null;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const getUserOfferOnCollectible = async (
    collectibleId: string,
    userId: string
  ) => {
    try {
      const response = await $serverApi<GFALResponse<OfferPayload | null>>(
        `/api/collectibles/${collectibleId}/offers`,
        {
          query: {
            buyer: userId,
          },
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response.payload?.records[0] || null;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const makeOfferOnCollectible = async (
    collectibleId: string,
    amountGFAL: string
  ) => {
    const response = await $serverApi<GFALResponse<Offer | null>>(
      `/api/collectibles/${collectibleId}/offers`,
      {
        method: "POST",
        body: {
          amountGFAL,
        },
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const signOfferOnCollectible = async (
    collectibleId: string,
    offerId: string,
    operationHash: string,
    code: string
  ) => {
    const signature = await web3Sign(operationHash);
    const response = await $serverApi<GFALResponse<Offer | null>>(
      `/api/collectibles/${collectibleId}/offers/${offerId}/sign`,
      {
        method: "POST",
        body: {
          signature,
          code,
        },
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const acceptOfferOnCollectible = async (
    collectibleId: string,
    offerId: string
  ) => {
    const response = await $serverApi<GFALResponse<Offer | null>>(
      `/api/collectibles/${collectibleId}/offers/${offerId}/accept`,
      {
        method: "POST",
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const cancelOfferOnCollectible = async (
    collectibleId: string,
    offerId: string
  ) => {
    try {
      const response = await $serverApi<GFALResponse<Offer | null>>(
        `/api/collectibles/${collectibleId}/offers/${offerId}`,
        {
          method: "DELETE",
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const getOwnershipHistory = async (
    collectibleId: string,
    page: number,
    pageSize: number
  ) => {
    try {
      const response = await $serverApi<GFALResponse<OwnershipHistory | null>>(
        `/api/collectibles/${collectibleId}/traces`,
        {
          query: {
            page,
            pageSize,
          },
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response.payload;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const listCollectible = async (collectibleId: string, priceGFAL: string) => {
    const response = await $serverApi<GFALResponse<Collectible | null>>(
      `/api/collectibles/${collectibleId}/list`,
      {
        method: "POST",
        body: {
          priceGFAL,
        },
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const unlistCollectible = async (collectibleId: string) => {
    try {
      const response = await $serverApi<GFALResponse<Collectible | null>>(
        `/api/collectibles/${collectibleId}/list`,
        {
          method: "DELETE",
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const getCollectibleFilters = async (
    merchantId: string,
    setId?: string,
    listed?: boolean,
    propertyType?: string[]
  ) => {
    try {
      const response = await $serverApi<GFALResponse<FiltersPayload | null>>(
        "/api/collectibles/filters",
        {
          query: {
            merchantId,
            setId,
            listed,
            propertyType: propertyType?.join(","),
          },
          headers: constructHeaders(userStore.authToken),
        }
      );
      return response.payload;
    } catch (error) {
      console.error("error", error);
      return null;
    }
  };

  const create = async (identifier: Identifier) => {
    const collectibleIdentifier = identifier as CollectibleIdentifier;
    const response = await $serverApi<GFALResponse<Purchase>>(
      `/api/collectibles/${collectibleIdentifier.id}/purchases`,
      {
        method: "POST",
        body: {
          collectibleId: collectibleIdentifier.id,
          gfalId: collectibleIdentifier.ownerId,
          price: collectibleIdentifier.price,
          level: collectibleIdentifier.level,
        },
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  const sign = async (
    identifier: Identifier,
    purchaseId: string,
    operationHash: string,
    code?: string
  ) => {
    const collectibleIdentifier = identifier as CollectibleIdentifier;
    const signature = await web3Sign(operationHash);
    const response = await $serverApi<GFALResponse<Purchase>>(
      `/api/collectibles/${collectibleIdentifier.id}/purchases/${purchaseId}/sign`,
      {
        method: "POST",
        body: {
          signature,
          code,
        },
        headers: constructHeaders(userStore.authToken),
      }
    );
    return response;
  };

  return {
    getCollectibles,
    getCollectiblesCount,
    getStackedCollectibles,
    getStackedCollectiblesCount,
    getCollectibleById,
    getCollectibleSet,
    getCheapestCollectibleInSet,
    setCollectibleAsViewed,
    getCollectibleOffers,
    getCollectibleHighestOffer,
    getUserOfferOnCollectible,
    makeOfferOnCollectible,
    signOfferOnCollectible,
    acceptOfferOnCollectible,
    cancelOfferOnCollectible,
    getOwnershipHistory,
    listCollectible,
    unlistCollectible,
    getCollectibleFilters,
    create,
    sign,
  };
};
